// The place card's photo gallery (WS-49 T5): the hero photo with a count on the card, opening a full-size viewer
// for mouse, keyboard and touch. One photo renders exactly what the card shows on main; no photo renders nothing.
// The logic is the pure functions at the top (unit-tested without a browser); the DOM code below is thin.
import { t } from './settings/index.js';
import { html, raw, safeUrl } from './dom.js';
import { cardContext } from './booking.js';
import { track } from './track.js';

/** @typedef {import('./booking.js').CardContext} CardContext */

export const MAX_PHOTOS = 12;

/**
 * The photos of a card: its `gallery` (a path, or `{ src, alt }` with a translatable alt), else its `image`.
 * Paths arrive loadable, as `image` does from content.js. Unsafe addresses, repeats and entries without a path
 * (an asset reference gets its address when the bundle is compiled) are left out.
 * @param {object} place  the card data of placeForKey(key)
 * @returns {Array<{ src: string, alt: string }>}
 */
export function photosOf(place, lang, fallbacks, base) {
  const pick = (items) => {
    const out = [];
    for (const item of items) {
      const src = safeUrl(typeof item === 'string' ? item : item?.src, base);
      if (src && out.length < MAX_PHOTOS && !out.some((p) => p.src === src)) out.push({ src, alt: t(item?.alt, lang, fallbacks) });
    }
    return out;
  };
  const photos = pick(Array.isArray(place?.gallery) ? place.gallery : []);
  return photos.length ? photos : pick([place?.image]);
}

/** The photo `by` steps from `i` among `n`, wrapping at both ends. */
export const stepIndex = (i, by, n) => (n > 0 ? (((i + by) % n) + n) % n : 0);

/** The photos worth preloading while `i` is on screen: its neighbours and nothing else. */
export const neighbours = (i, n) => [...new Set([stepIndex(i, -1, n), stepIndex(i, 1, n)])].filter((k) => k !== i);

const KEYS = new Map([['ArrowLeft', 'prev'], ['ArrowRight', 'next'], ['Home', 'first'], ['End', 'last'], ['Escape', 'close']]);
/** What a key does in the open viewer: `prev`, `next`, `first`, `last`, `close`, or null (not ours). */
export const keyAction = (key) => KEYS.get(key) || null;

/** What a finished drag does: a mostly horizontal move of 40 px or more turns the page, anything else nothing. */
export const swipeAction = (dx, dy) => (Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy) * 1.5 ? (dx < 0 ? 'next' : 'prev') : null);

/** The photo on screen after an action (`close` and unknown actions leave it where it is). */
export function moveTo(i, action, n) {
  if (action === 'prev' || action === 'next') return stepIndex(i, action === 'next' ? 1 : -1, n);
  return action === 'first' ? 0 : action === 'last' ? Math.max(0, n - 1) : i;
}

/**
 * Keeps Tab inside the viewer: with `n` buttons and focus on the `i`-th (-1 = outside), the index to focus
 * instead of the browser's choice, or -1 when the browser may move on by itself.
 */
export const tabWrap = (n, i, back) => (n < 1 ? -1 : i < 0 ? (back ? n - 1 : 0) : back && i === 0 ? n - 1 : !back && i === n - 1 ? 0 : -1);

// ------------------------------------------------------------------------------------------------ DOM
const ic = (d) => raw(`<svg class="ic" viewBox="0 0 24 24" aria-hidden="true">${d}</svg>`); // constants only
const ICON = {
  close: ic('<path d="m6 6 12 12M18 6 6 18"/>'),
  prev: ic('<path d="M15 5 8 12l7 7"/>'),
  next: ic('<path d="m9 5 7 7-7 7"/>'),
  photos: ic('<rect x="3.5" y="7.5" width="14" height="12" rx="2.5"/><path d="M7.5 4.5H18a2.5 2.5 0 0 1 2.5 2.5v9"/>'),
};

// index.html links css/gallery.css; a page that leaves the link out gets it when the first gallery renders.
function ensureCss() {
  if (document.querySelector('link[href$="css/gallery.css"]')) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = 'css/gallery.css';
  document.head.append(link);
}

/**
 * The gallery of a place card, or null when the place has no photo. One photo: the plain hero image of main.
 * More: the first photo as a button with the count, which opens the viewer.
 * @param {object} place  the card data of placeForKey(key)
 * @param {CardContext} [ctx]
 * @returns {HTMLElement | null}
 */
export function renderGallery(place, ctx) {
  const c = cardContext(ctx);
  const photos = photosOf(place, c.lang, c.fallback);
  if (!photos.length) return null;
  const img = document.createElement('img');
  img.src = photos[0].src;
  img.alt = photos.length === 1 ? photos[0].alt : ''; // inside the button the label speaks
  if (photos.length === 1) return img;
  ensureCss();
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'gal';
  btn.dataset.control = 'card.gallery'; // every new control's root says which control it is (tests, the editor)
  btn.setAttribute('aria-haspopup', 'dialog');
  btn.setAttribute('aria-label', c.ui('gallery.open').replace('{n}', String(photos.length)));
  btn.innerHTML = html`<span class="gal-count" aria-hidden="true">${ICON.photos}${photos.length}</span>`;
  btn.prepend(img);
  btn.addEventListener('click', () => openViewer(photos, place.id, t(place.title, c.lang, c.fallback), btn, c));
  return btn;
}

function openViewer(photos, target, title, opener, c) {
  let i = 0, down = null, swiped = false;
  const view = document.createElement('div');
  view.className = 'gal-view';
  view.tabIndex = -1; // a press on the photo keeps the focus in the viewer
  view.setAttribute('role', 'dialog');
  view.setAttribute('aria-modal', 'true');
  view.setAttribute('aria-label', title);
  view.innerHTML = html`<button type="button" class="round-btn gal-close" data-go="close" aria-label="${c.ui('gallery.close')}">${ICON.close}</button>
    <button type="button" class="round-btn gal-prev" data-go="prev" aria-label="${c.ui('gallery.prev')}">${ICON.prev}</button>
    <figure class="gal-stage"><img alt="" draggable="false"><figcaption></figcaption></figure>
    <button type="button" class="round-btn gal-next" data-go="next" aria-label="${c.ui('gallery.next')}">${ICON.next}</button>
    <p class="gal-n" aria-live="polite"></p>`;
  const stage = view.querySelector('.gal-stage'), img = stage.firstElementChild;

  const show = (k) => {
    i = k;
    img.src = photos[i].src;
    img.alt = photos[i].alt || title;
    stage.lastElementChild.textContent = photos[i].alt;
    view.querySelector('.gal-n').textContent = `${i + 1} / ${photos.length}`;
    for (const j of neighbours(i, photos.length)) new Image().src = photos[j].src;
  };
  const act = (a) => (a === 'close' ? close() : a && show(moveTo(i, a, photos.length)));
  // On the window, before the map's own shortcuts: arrows would pan the map and Esc would close the card.
  const onKey = (e) => {
    e.stopPropagation();
    const a = keyAction(e.key);
    if (a) { e.preventDefault(); act(a); return; }
    if (e.key !== 'Tab') return;
    const buttons = [...view.querySelectorAll('button')];
    const k = tabWrap(buttons.length, buttons.indexOf(document.activeElement), e.shiftKey);
    if (k >= 0) { e.preventDefault(); buttons[k].focus(); }
  };
  const off = c.runtime?.on?.('select', () => close()); // the card moved on (a tour, a deep link): the viewer goes with it
  function close() {
    removeEventListener('keydown', onKey, true);
    if (typeof off === 'function') off();
    view.remove();
    if (opener.isConnected) opener.focus();
  }

  view.addEventListener('click', (e) => {
    if (swiped) { swiped = false; return; } // the click that ends a mouse drag
    act(e.target.closest('[data-go]')?.dataset.go || (e.target === view || e.target === stage ? 'close' : null));
  });
  view.addEventListener('pointerdown', (e) => { swiped = false; down = e.isPrimary ? [e.clientX, e.clientY] : null; });
  view.addEventListener('pointerup', (e) => {
    const a = down && swipeAction(e.clientX - down[0], e.clientY - down[1]);
    down = null;
    swiped = !!a;
    act(a);
  });
  view.addEventListener('pointercancel', () => { down = null; });
  addEventListener('keydown', onKey, true);
  document.body.append(view);
  show(0);
  view.firstElementChild.focus();
  track('gallery_open', { target });
}
